import {
    Box,
    Button,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useEffect, useState} from "react";
import {formatHuman} from "../utils/dateUtils";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";

const WEEKDAYS = ["mo", "tu", "we", "th", "fr", "sa", "su"];
const WEEKDAY_LABELS = ["H", "K", "Sze", "Cs", "P", "Szo", "V"];

export default function RecurrenceSelector({data, onChange}) {
    const [state, setState] = useState({
        start_date: formatHuman(new Date()),
        frequency: "weekly",
        interval: 1,
        weekdays: ["mo", "tu", "we", "th", "fr"],
        monthly: {
            mode: "day_of_month",
            day: 1,
            nth: 1,
            weekday:  "mo"
        },
        end: "never",
        endDate: null,
        occurrenceCount: null,
        ...data
    })
    const update = (patch) => setState({...state, ...patch});

    useEffect(() => {
        onChange(state)
    }, [state])

    return (
        <Stack spacing={3}>
            {/* ------------------ KEZDETE ------------------ */}
            <Section title="Kezdete">
                <DatePicker
                    value={state.start_date ?? ""}
                    onChange={newValue => update({start_date: newValue ? dayjs(newValue).format('YYYY-MM-DD') : null})}
                    label='Kezdete'
                    renderInput={(params) => <TextField fullWidth variant='standard' {...params} />}
                    disablePast
                />
            </Section>
            {/* ------------------ FREKVENCIA ------------------ */}
            <Section title="Ismétlődés">
                <Select
                    fullWidth
                    variant='standard'
                    value={state.frequency}
                    onChange={(e) => update({frequency: e.target.value})}
                >
                    <MenuItem value="once">Nincs ismétlődés</MenuItem>
                    <MenuItem value="daily">Naponta</MenuItem>
                    <MenuItem value="weekly">Hetente</MenuItem>
                    <MenuItem value="monthly">Havonta</MenuItem>
                </Select>
            </Section>

            {/* ------------------ INTERVALLUM ------------------ */}
            {state.frequency !== "once" && (
                <Section title="Ismétlés gyakorisága">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            type="number"
                            variant='standard'
                            value={state.interval}
                            onChange={(e) =>
                                update({interval: Math.max(1, Number(e.target.value))})
                            }
                            sx={{width: 100}}
                        />
                        <Typography>
                            {state.frequency === "daily" && "naponként"}
                            {state.frequency === "weekly" && "hetenként"}
                            {state.frequency === "monthly" && "havonta"}
                        </Typography>
                    </Stack>
                </Section>
            )}

            {/* ------------------ HETI ------------------ */}
            {state.frequency === "weekly" && (
                <Section title="Napok">
                    <Stack direction="row" spacing={1}>
                        {WEEKDAYS.map((day, i) => {
                            const active = state.weekdays?.includes(day);
                            return (
                                <Button
                                    key={day}
                                    variant={active ? "contained" : "outlined"}
                                    onClick={() => {
                                        const set = new Set(state.weekdays ?? []);
                                        set.has(day) ? set.delete(day) : set.add(day);
                                        update({weekdays: Array.from(set)});
                                    }}
                                    sx={{minWidth: 40, borderRadius: 20}}
                                >
                                    {WEEKDAY_LABELS[i]}
                                </Button>
                            );
                        })}
                    </Stack>
                </Section>
            )}

            {/* ------------------ HAVI ------------------ */}
            {state.frequency === "monthly" && (
                <Section title="Havi beállítások">
                    <RadioGroup
                        value={state.monthly?.mode}
                        onChange={(e) =>
                            update({monthly: {...state.monthly, mode: e.target.value}})
                        }
                    >
                        {/* NAP SZERINT */}
                        <FormControlLabel
                            value="day_of_month"
                            control={<Radio/>}
                            label={
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography>Adott napon</Typography>
                                    {state.monthly?.mode === "day_of_month" && (
                                        <TextField
                                            variant='standard'
                                            type="number"
                                            value={state.monthly?.day ?? 1}
                                            onChange={(e) =>
                                                update({
                                                    monthly: {
                                                        ...state.monthly,
                                                        day: Number(e.target.value)
                                                    }
                                                })
                                            }
                                            sx={{width: 80}}
                                        />
                                    )}
                                </Stack>
                            }
                        />

                        {/* N-edik hét napja */}
                        <FormControlLabel
                            value="nth_weekday"
                            control={<Radio/>}
                            label={
                                <Stack spacing={2}>
                                    <Typography>Adott hét napján (N-edik)</Typography>

                                    {state.monthly?.mode === "nth_weekday" && (
                                        <Stack direction="row" spacing={2}>
                                            <Select
                                                variant='standard'
                                                value={state.monthly?.nth ?? 1}
                                                onChange={(e) =>
                                                    update({
                                                        monthly: {
                                                            ...state.monthly,
                                                            nth: e.target.value
                                                        }
                                                    })
                                                }
                                                sx={{width: 120}}
                                            >
                                                <MenuItem value={1}>1.</MenuItem>
                                                <MenuItem value={2}>2.</MenuItem>
                                                <MenuItem value={3}>3.</MenuItem>
                                                <MenuItem value={4}>4.</MenuItem>
                                                <MenuItem value={-1}>Utolsó</MenuItem>
                                            </Select>

                                            <Select
                                                variant='standard'
                                                value={state.monthly?.weekday ?? "mo"}
                                                onChange={(e) =>
                                                    update({
                                                        monthly: {
                                                            ...state.monthly,
                                                            weekday: e.target.value
                                                        }
                                                    })
                                                }
                                                sx={{width: 120}}
                                            >
                                                {["Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"].map(
                                                    (label, i) => (
                                                        <MenuItem key={WEEKDAYS[i]} value={WEEKDAYS[i]}>
                                                            {label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </Stack>
                                    )}
                                </Stack>
                            }
                        />
                    </RadioGroup>
                </Section>
            )}

            {/* ------------------ VÉGE ------------------ */}
            {state.frequency !== "once" && (
                <Section title="Befejezés">
                    <RadioGroup
                        value={state.end}
                        onChange={(e) => update({end: e.target.value})}
                    >
                        <FormControlLabel
                            value="never"
                            control={<Radio/>}
                            label="Soha"
                        />

                        <FormControlLabel
                            value="on_date"
                            control={<Radio/>}
                            label={
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography>Dátummal</Typography>
                                    {state.end === "on_date" && (
                                        <DatePicker
                                            value={state.endDate ?? ""}
                                            onChange={newValue => update({endDate: newValue ? dayjs(newValue).format('YYYY-MM-DD') : null})}
                                            label='Befejezés'
                                            renderInput={(params) => <TextField fullWidth
                                                                                variant='standard' {...params} />}
                                            disablePast
                                        />
                                    )}
                                </Stack>
                            }
                        />

                        {/*<FormControlLabel*/}
                        {/*    value="after_count"*/}
                        {/*    control={<Radio/>}*/}
                        {/*    label={*/}
                        {/*        <Stack direction="row" spacing={2} alignItems="center">*/}
                        {/*            <Typography>Később,</Typography>*/}
                        {/*            {state.end === "after_count" && (*/}
                        {/*                <TextField*/}
                        {/*                    variant='standard'*/}
                        {/*                    sx={{width: 100}}*/}
                        {/*                    type="number"*/}
                        {/*                    value={state.occurrenceCount ?? 1}*/}
                        {/*                    onChange={(e) =>*/}
                        {/*                        update({*/}
                        {/*                            occurrenceCount: Number(e.target.value)*/}
                        {/*                        })*/}
                        {/*                    }*/}
                        {/*                />*/}
                        {/*            )}*/}
                        {/*            {state.end === "after_count" && (*/}
                        {/*                <Typography>alkalom után</Typography>*/}
                        {/*            )}*/}
                        {/*        </Stack>*/}
                        {/*    }*/}
                        {/*/>*/}
                    </RadioGroup>
                </Section>
            )}

            {/* ------------------ ÖSSZEFOGLALÓ ------------------ */}
            <Summary rule={state}/>
        </Stack>
    );
}

function Section({title, children}) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{mb: 1, color: "text.secondary"}}>
                {title}
            </Typography>
            {children}
        </Box>
    );
}

function Summary({rule}) {
    return (
        <Typography sx={{color: "text.secondary", mt: 2, fontStyle: "italic"}}>
            {humanize(rule)}
        </Typography>
    );
}

export function humanize(rule) {
    if (rule.frequency === "once") return "Nincs ismétlődés.";

    let base = "Minden ";

    if (rule.interval > 1) base += rule.interval + " ";

    if (rule.frequency === "daily") base += "nap";
    if (rule.frequency === "weekly") base += "hét";
    if (rule.frequency === "monthly") base += "hónap";

    if (rule.frequency === "weekly" && rule.weekdays?.length) {
        base += " " + rule.weekdays
            .map(w => WEEKDAY_LABELS[WEEKDAYS.indexOf(w)])
            .join(", ");
    }

    if (rule.frequency === "monthly") {
        if (rule.monthly?.mode === "day_of_month") {
            base += ` adott napon (${rule.monthly.day}. nap)`;
        } else {
            base += ` az ${nthLabel(rule.monthly.nth)} ${hungarianWeekday(rule.monthly.weekday)}`;
        }
    }

    if (rule.end === "on_date") base += `, eddig: ${rule.endDate}`;
    if (rule.end === "after_count") base += `, ${rule.occurrenceCount} alkalom után vége`;

    return base + ".";
}

function nthLabel(n) {
    if (n === -1) return "utolsó";
    return `${n}.`;
}

function hungarianWeekday(w) {
    const map = {
        mo: "hétfő",
        tu: "kedd",
        we: "szerda",
        th: "csütörtök",
        fr: "péntek",
        sa: "szombat",
        su: "vasárnap"
    };
    return map[w];
}
